import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { logger } from '../shared/logger';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';

export const EmailVerification: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { verifyEmail, isEmailVerified, currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [verificationComplete, setVerificationComplete] = useState(false);
  const [isPPU, setIsPPU] = useState(false);

  useEffect(() => {
    const oobCode = searchParams.get('oobCode');
    if (!oobCode) {
      setError('Invalid verification link');
      setLoading(false);
      return;
    }

    const verify = async () => {
      try {
        await verifyEmail(oobCode);
        setVerificationComplete(true);
        logger.info('Email verification successful');

        // Check if this is a PPU facility
        if (currentUser) {
          const facilityId = localStorage.getItem('facilityId');
          if (facilityId) {
            const facilityRef = doc(db, 'facilities', facilityId);
            const facilityDoc = await getDoc(facilityRef);
            if (facilityDoc.exists()) {
              const facilityData = facilityDoc.data();
              setIsPPU(facilityData.isPPU === true);
            }
          }
        }
      } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err));
        logger.error('Email verification error:', { error: error.message });
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    verify();
  }, [searchParams, verifyEmail, currentUser]);

  const handleContinue = () => {
    if (isEmailVerified) {
      const businessId = localStorage.getItem('businessId');
      // Always redirect to login for both PPU and regular users
      navigate(`/member/login?verification=success&businessId=${businessId}`);
    } else {
      navigate('/member/login?verification=failed');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
              Verifying Email
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              Please wait while we verify your email...
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
              Verification Failed
            </h2>
            <p className="mt-2 text-sm text-red-600">
              {error}
            </p>
            <button
              onClick={() => navigate('/member/login')}
              className="mt-4 w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Return to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
            {verificationComplete ? 'Email Verified!' : 'Verification Complete'}
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            {verificationComplete 
              ? isPPU 
                ? 'Your email has been successfully verified. You can now access your dashboard.'
                : 'Your email has been successfully verified. You can now continue to login.'
              : 'Your email verification is complete. Please continue to login.'}
          </p>
          <button
            onClick={handleContinue}
            className="mt-4 w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            {isPPU ? 'Complete Registration' : 'Continue to Login'}
          </button>
        </div>
      </div>
    </div>
  );
}; 